'use client';

import { useEffect, useRef } from 'react';
import { BoardMessageType, type BoardMessage } from '../types';
import type { DeviceStorage } from '../deviceStorage';
import type { CheckHintActions } from './playState';

/*
  Holding Undo resets the puzzle, which nothing on screen says. A player undoing move after move is the one who
  wants to know, so a run of quick undos shows a tip in the hint box: at most once a puzzle and a few times in
  all, and never once they've reset that way themselves.

  The tip is a board message like any other, so the next move clears it. Each further undo of the run is such a
  move, and puts it back.

  A reset by holding is itself undoable, and says so in the same box: a hold nobody meant costs one more Undo.
*/

/** How long Undo is held before it resets, on every platform. */
export const UNDO_HOLD_MS = 1000;

const STREAK_PRESSES = 6;
const STREAK_MS = 4000;
export const UNDO_TIP_MAX_SHOWS = 3;

export const undoTipMessage: BoardMessage = {
  type: BoardMessageType.hint,
  message: 'Press and hold Undo to reset the board.',
  markedCells: [],
};

export const undoResetMessage: BoardMessage = {
  type: BoardMessageType.hint,
  message: 'Board reset. Press Undo to bring it back.',
  markedCells: [],
};

/** Where the device keeps how many times the tip has been shown. */
export const UNDO_TIP_SHOWN_COUNT_KEY = 'undo_tip_shown_count';

// The puzzle the tip was last shown on. Outside the hook so every Undo button a page mounts shares it.
let lastTipPuzzle: string | null = null;

/** For tests, which otherwise carry the last tip's puzzle from one to the next. */
export function resetUndoTipForTests() {
  lastTipPuzzle = null;
}

/**
 * The Undo button's two actions, with the tip above layered on: `onUndo` for a press and `onUndoLongPress` for a
 * hold. `puzzleKey` names the puzzle being played; `enabled` is false where the hint box is spoken for, as in a
 * tutorial.
 */
export function useUndoTip({
  handleUndo,
  handleReset,
  checkHint,
  puzzleKey,
  storage,
  enabled = true,
}: {
  handleUndo: () => void;
  handleReset: (options?: { undoable?: boolean }) => void;
  checkHint: Pick<CheckHintActions, 'setHintBoxState' | 'setBoardMessages'>;
  puzzleKey: string;
  storage: DeviceStorage;
  enabled?: boolean;
}) {
  // Null until read, and the tip waits for it: a count that hasn't loaded mustn't pass for zero.
  const shownCount = useRef<number | null>(null);
  const presses = useRef<number[]>([]);
  // Whether the tip is up for the current run of undos, and so wants putting back after each one.
  const tipUp = useRef(false);

  useEffect(() => {
    let current = true;
    // Storage that can't be read leaves the count unknown, and so the tip unshown.
    Promise.resolve()
      .then(() => storage.get(UNDO_TIP_SHOWN_COUNT_KEY))
      .then((stored) => {
        if (current) shownCount.current ??= parseInt(stored ?? '0', 10) || 0;
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [storage]);

  const record = (count: number) => {
    shownCount.current = count;
    // Unsaved, the limits last only as long as the page does.
    Promise.resolve()
      .then(() => storage.set(UNDO_TIP_SHOWN_COUNT_KEY, String(count)))
      .catch(() => {});
  };

  const onUndo = () => {
    handleUndo();
    if (!enabled) return;

    const now = Date.now();
    const last = presses.current[presses.current.length - 1];
    if (last === undefined || now - last > STREAK_MS) tipUp.current = false;
    presses.current = [...presses.current, now].filter((at) => now - at <= STREAK_MS);

    if (!tipUp.current) {
      if (presses.current.length < STREAK_PRESSES || lastTipPuzzle === puzzleKey) return;
      if (shownCount.current === null || shownCount.current >= UNDO_TIP_MAX_SHOWS) return;
      record(shownCount.current + 1);
      lastTipPuzzle = puzzleKey;
      tipUp.current = true;
    }
    checkHint.setHintBoxState('tip');
    checkHint.setBoardMessages([undoTipMessage]);
  };

  const onUndoLongPress = () => {
    handleReset({ undoable: true });
    presses.current = [];
    tipUp.current = false;
    // They know now, so the tip has nothing left to teach.
    record(UNDO_TIP_MAX_SHOWS);
    if (!enabled) return;
    checkHint.setHintBoxState('tip');
    checkHint.setBoardMessages([undoResetMessage]);
  };

  return { onUndo, onUndoLongPress };
}
